import { type ReactNode, useEffect, useRef, useState, useSyncExternalStore } from "react";
import type { ConnectionStatus } from "@/hooks/use-ipc-connection";
import { AccountSettingsContext } from "./account-settings-context";
import { type AccountSettingsStore, createAccountSettingsStore } from "./account-settings-store";

interface AccountSettingsProviderProps {
  login: string;
  /** Состояние потока §7.6: переход в `open` повторяет неотправленную запись. */
  connectionStatus: ConnectionStatus;
  onSessionInvalid: () => void;
  /** Показывается, пока настройки читаются: тот же индикатор, что при проверке сессии. */
  fallback: ReactNode;
  children: ReactNode;
}

function Gate({
  store,
  fallback,
  children,
}: {
  store: AccountSettingsStore;
  fallback: ReactNode;
  children: ReactNode;
}) {
  const read = () => store.getState().status;
  const status = useSyncExternalStore(store.subscribe, read, read);
  return status === "ready" ? children : fallback;
}

/**
 * Настройки аккаунта над экраном (§127, design.md stage9-account-settings, Д4):
 * экран не рисуется, пока чтение не завершилось успехом или отказом. Провайдер
 * живёт на одну сессию одного пользователя — смена логина пересоздаёт его
 * ключом снаружи, чтобы настройки одного не попали к другому.
 */
export function AccountSettingsProvider({
  login,
  connectionStatus,
  onSessionInvalid,
  fallback,
  children,
}: AccountSettingsProviderProps) {
  const [store, setStore] = useState<AccountSettingsStore | null>(null);
  const onSessionInvalidRef = useRef(onSessionInvalid);

  useEffect(() => {
    onSessionInvalidRef.current = onSessionInvalid;
  }, [onSessionInvalid]);

  // Хранилище создаётся в эффекте, а не при рендере: StrictMode гоняет эффект
  // дважды, и погашенное первым проходом хранилище нельзя переиспользовать.
  useEffect(() => {
    const created = createAccountSettingsStore(login, {
      fetch: (input, init) => fetch(input, init),
      // Геттер `window.localStorage` бросает при запрещённых данных сайта:
      // вызов обязан стоять внутри защиты вызывающего.
      storage: () => window.localStorage,
      setTimer: (run, ms) => setTimeout(run, ms),
      clearTimer: (id) => clearTimeout(id),
      now: () => Date.now(),
      onSessionInvalid: () => onSessionInvalidRef.current(),
    });
    setStore(created);
    void created.load();
    return () => {
      created.dispose();
    };
  }, [login]);

  useEffect(() => {
    if (store !== null && connectionStatus === "open") {
      store.notifyReconnected();
    }
  }, [store, connectionStatus]);

  if (store === null) {
    return fallback;
  }
  return (
    <AccountSettingsContext.Provider value={store}>
      <Gate store={store} fallback={fallback}>
        {children}
      </Gate>
    </AccountSettingsContext.Provider>
  );
}
